/**
 * Plan definitions with download limits
 * Synchronized with backend /backend/src/constants/billing.js
 */

export type PlanCode = "free" | "monthly" | "quarterly" | "monthly_pix" | "professional" | "annual_pix" | "annual_card" | "basic" | "premium-plus" | "premium-pro" | "crie_oferta" | "admin_premium";

export interface PlanFeatures {
  code: PlanCode;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: string | null;
  downloadLimitFree: number;
  downloadLimitPremium: number;
  features: string[];
  highlighted?: boolean;
  ctaLabel: string;
  paymentMethod?: "pix" | "card";
  recurrence?: boolean;
  couponAllowed?: boolean;
  legacy?: boolean;
}

export const PLAN_FEATURES: Record<PlanCode, PlanFeatures> = {
  free: {
    code: "free",
    name: "Membro Grátis",
    description: "Perfeito para começar",
    price: 0,
    currency: "BRL",
    interval: null,
    downloadLimitFree: 2,
    downloadLimitPremium: 0,
    features: [
      "2 downloads gratuitos por dia",
      "Acesso a arquivos gratuitos",
      "Sem acesso a arquivos premium",
      "Suporte básico"
    ],
    ctaLabel: "Plano Atual"
  },
  admin_premium: {
    code: "admin_premium",
    name: "Premium (Admin)",
    description: "Acesso premium concedido pelo administrador",
    price: 0,
    currency: "BRL",
    interval: null,
    downloadLimitFree: 999999,
    downloadLimitPremium: 999999,
    features: [
      "Downloads ilimitados",
      "Acesso total aos arquivos premium",
      "Concedido pelo administrador",
      "Suporte prioritário"
    ],
    ctaLabel: "Ativo"
  },
  crie_oferta: {
    code: "crie_oferta",
    name: "Plano Premium Crie Oferta",
    description: "Acesso premium vinculado ao Crie Oferta",
    price: 0,
    currency: "BRL",
    interval: null,
    downloadLimitFree: 999999,
    downloadLimitPremium: 999999,
    features: [
      "Downloads ilimitados",
      "Acesso total aos arquivos premium",
      "Vinculado à sua conta Crie Oferta",
      "Suporte prioritário"
    ],
    ctaLabel: "Ativo"
  },
  // ── Novos planos ativos ──
  monthly_pix: {
    code: "monthly_pix",
    name: "Mensal Livre",
    description: "Assinatura mensal via PIX — renovação manual",
    price: 31.90,
    currency: "BRL",
    interval: "mês",
    downloadLimitFree: 999999,
    downloadLimitPremium: 999999,
    features: [
      "Downloads ilimitados",
      "Acesso total aos arquivos premium",
      "Pagamento via PIX",
      "Renovação manual",
      "Suporte prioritário"
    ],
    ctaLabel: "Assinar com PIX",
    paymentMethod: "pix",
    recurrence: false,
    couponAllowed: false
  },
  professional: {
    code: "professional",
    name: "Profissional",
    description: "Assinatura mensal recorrente via cartão",
    price: 24.90,
    currency: "BRL",
    interval: "mês",
    downloadLimitFree: 999999,
    downloadLimitPremium: 999999,
    features: [
      "Downloads ilimitados",
      "Acesso total aos arquivos premium",
      "Pagamento via Cartão",
      "Renovação automática",
      "Aceita cupom de desconto",
      "Suporte prioritário"
    ],
    highlighted: true,
    ctaLabel: "Assinar com Cartão",
    paymentMethod: "card",
    recurrence: true,
    couponAllowed: true
  },
  annual_pix: {
    code: "annual_pix",
    name: "Anual PIX",
    description: "Assinatura anual via PIX — renovação manual",
    price: 279.00,
    currency: "BRL",
    interval: "ano",
    downloadLimitFree: 999999,
    downloadLimitPremium: 999999,
    features: [
      "Downloads ilimitados",
      "Acesso total aos arquivos premium",
      "Pagamento via PIX",
      "Renovação manual",
      "Economia anual",
      "Suporte prioritário"
    ],
    ctaLabel: "Assinar Anual PIX",
    paymentMethod: "pix",
    recurrence: false,
    couponAllowed: false
  },
  annual_card: {
    code: "annual_card",
    name: "Anual Cartão",
    description: "Assinatura anual recorrente via cartão",
    price: 249.00,
    currency: "BRL",
    interval: "ano",
    downloadLimitFree: 999999,
    downloadLimitPremium: 999999,
    features: [
      "Downloads ilimitados",
      "Acesso total aos arquivos premium",
      "Pagamento via Cartão",
      "Renovação automática",
      "Melhor custo-benefício",
      "Suporte prioritário"
    ],
    ctaLabel: "Assinar Anual Cartão",
    paymentMethod: "card",
    recurrence: true,
    couponAllowed: false
  },
  // ── Planos legados (não vendidos, mantidos para usuários existentes) ──
  monthly: {
    code: "monthly",
    name: "Plano Mensal (Legado)",
    description: "Plano legado — não disponível para novas assinaturas",
    price: 19.90,
    currency: "BRL",
    interval: "mês",
    downloadLimitFree: 999999,
    downloadLimitPremium: 999999,
    features: [
      "Downloads ilimitados",
      "Acesso total aos arquivos premium",
      "Suporte prioritário"
    ],
    ctaLabel: "Indisponível",
    legacy: true
  },
  quarterly: {
    code: "quarterly",
    name: "Plano Trimestral (Legado)",
    description: "Plano legado — não disponível para novas assinaturas",
    price: 49.90,
    currency: "BRL",
    interval: "trimestre",
    downloadLimitFree: 999999,
    downloadLimitPremium: 999999,
    features: [
      "Downloads ilimitados",
      "Acesso total aos arquivos premium",
      "Suporte prioritário"
    ],
    ctaLabel: "Indisponível",
    legacy: true
  },
  basic: {
    code: "basic",
    name: "Membro Básico",
    description: "Para criadores iniciantes",
    price: 29.90,
    currency: "BRL",
    interval: "mês",
    downloadLimitFree: 5,
    downloadLimitPremium: 6,
    features: [
      "6 downloads premium por dia",
      "5 downloads gratuitos por dia",
      "Acesso total aos arquivos premium",
      "Suporte prioritário",
      "Editor online 1-clique"
    ],
    ctaLabel: "Assinar Agora"
  },
  "premium-plus": {
    code: "premium-plus",
    name: "Membro Premium+",
    description: "Para profissionais exigentes",
    price: 59.90,
    currency: "BRL",
    interval: "mês",
    downloadLimitFree: 10,
    downloadLimitPremium: 20,
    features: [
      "20 downloads premium por dia",
      "10 downloads gratuitos por dia",
      "Acesso ilimitado premium",
      "Suporte VIP 24/7",
      "Editor online avançado",
      "Preview de PSD completo",
      "Downloads prioritários"
    ],
    highlighted: true,
    ctaLabel: "Mais Popular"
  },
  "premium-pro": {
    code: "premium-pro",
    name: "Membro Premium Pro",
    description: "Para agências e equipes",
    price: 99.90,
    currency: "BRL",
    interval: "mês",
    downloadLimitFree: 8,
    downloadLimitPremium: 12,
    features: [
      "12 downloads premium por dia",
      "8 downloads gratuitos por dia",
      "Licença comercial estendida",
      "Gerenciamento de equipe",
      "API de integração",
      "Suporte dedicado",
      "Relatórios personalizados"
    ],
    ctaLabel: "Começar Agora"
  }
};

/**
 * Get download limits for a plan
 */
export const getDownloadLimits = (planCode: PlanCode | null | undefined) => {
  const code = planCode && PLAN_FEATURES[planCode] ? planCode : "free";
  const plan = PLAN_FEATURES[code];
  return {
    free: plan.downloadLimitFree,
    premium: plan.downloadLimitPremium,
    total: plan.downloadLimitFree + plan.downloadLimitPremium
  };
};

/**
 * Check if a plan code is a premium plan with unlimited downloads
 */
export const isPremiumPlan = (planCode: PlanCode | string | null | undefined): boolean => {
  if (!planCode) return false;
  // Planos premium ativos + legados
  return (
    planCode === "monthly_pix" ||
    planCode === "professional" ||
    planCode === "annual_pix" ||
    planCode === "annual_card" ||
    planCode === "monthly" ||
    planCode === "quarterly" ||
    planCode === "crie_oferta" ||
    planCode === "admin_premium"
  );
};

/**
 * Check if a plan has unlimited downloads
 */
export const hasUnlimitedDownloads = (planCode: PlanCode | null | undefined): boolean => {
  const limits = getDownloadLimits(planCode);
  return limits.free >= 999999 || limits.premium >= 999999;
};

/**
 * Format plan price for display
 */
export const formatPlanPrice = (planCode: PlanCode) => {
  const plan = PLAN_FEATURES[planCode] ?? PLAN_FEATURES.free;
  if (plan.price === 0) return "Grátis";
  
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: plan.currency
  }).format(plan.price);
};

/**
 * Get plan display name with interval
 */
export const getPlanDisplayName = (planCode: PlanCode) => {
  const plan = PLAN_FEATURES[planCode] ?? PLAN_FEATURES.free;
  if (!plan.interval) return plan.name;
  return `${plan.name} - ${formatPlanPrice(planCode)}/${plan.interval}`;
};

/**
 * Check if user can download based on plan and usage
 */
export const canDownloadArtwork = (
  planCode: PlanCode | null | undefined,
  isArtworkPremium: boolean,
  freeDownloadsToday: number,
  premiumDownloadsToday: number
): { canDownload: boolean; reason: string | null } => {
  const limits = getDownloadLimits(planCode);
  
  if (isArtworkPremium) {
    if (limits.premium === 0) {
      return {
        canDownload: false,
        reason: "Seu plano não permite downloads de arquivos premium. Faça upgrade!"
      };
    }
    
    if (premiumDownloadsToday >= limits.premium) {
      return {
        canDownload: false,
        reason: `Limite diário de downloads premium atingido (${limits.premium}/${limits.premium})`
      };
    }
  } else {
    if (freeDownloadsToday >= limits.free) {
      return {
        canDownload: false,
        reason: `Limite diário de downloads gratuitos atingido (${limits.free}/${limits.free})`
      };
    }
  }
  
  return { canDownload: true, reason: null };
};
